// Build an embed URL for a Vimeo or YouTube video.
//
// Vimeo unlisted videos require a privacy hash (the `h=` query param). Without
// it, the iframe gets a 403. This one helper is the single recognizer for every
// shape an admin might paste — keep new shapes here rather than re-deriving the
// logic per page (see
// docs/solutions/integration-issues/vimeo-embed-privacy-hash-2026-04-28.md).
// The shapes Vimeo hands you:
//   - "https://vimeo.com/734454683/1e229a8ddc"   (share link, hash as path segment)
//   - "https://vimeo.com/734454683?h=1e229a8ddc" (share link, hash as query)
//   - "https://vimeo.com/734454683"              (hash-less public share link)
//   - "https://vimeo.com/manage/videos/734454683/1e229a8ddc" (address-bar URL)
//   - "https://player.vimeo.com/video/734454683?h=1e229a8ddc" (already an embed URL)
//   - '<iframe src="https://player.vimeo.com/video/734454683?h=1e229a8ddc" …>' (embed snippet)
//   - "734454683?h=1e229a8ddc"                   (bare ID + hash)
//   - "734454683"                                (bare ID, public video)
//
// YouTube accepts watch URLs, short youtu.be URLs, and embed URLs.
//
// Returns the embed URL string, or null when the input is not recognized as a
// video URL from a supported provider. A bare vimeo.com *website* URL (no
// numeric id) returns null — passing it to an iframe just loads the Vimeo page,
// which refuses framing.

const VIMEO_EXTRA_PARAMS = "title=0&byline=0&portrait=0"

function vimeoEmbed(id, hash) {
  const hashParam = hash ? `h=${hash}&` : ""
  return `https://player.vimeo.com/video/${id}?${hashParam}${VIMEO_EXTRA_PARAMS}`
}

export function buildVideoEmbedUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null
  const url = rawUrl.trim()
  if (!url) return null

  // Pasted embed snippet — pull the src out of the <iframe> and resolve that.
  // Checked FIRST: the snippet contains "player.vimeo.com", so the passthrough
  // below would otherwise return the whole HTML string.
  if (url.includes("<iframe")) {
    const src = url.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i)
    if (src) return buildVideoEmbedUrl(src[1])
  }

  // Already a Vimeo player embed URL — pass through.
  if (url.includes("player.vimeo.com")) return url

  // Vimeo address-bar "manage" URL: vimeo.com/manage/videos/{id}[/{hash}].
  // Checked before the generic patterns because "manage" is not a numeric id.
  const vimeoManage = url.match(/vimeo\.com\/manage\/videos\/(\d+)(?:\/([a-zA-Z0-9]+))?/)
  if (vimeoManage) return vimeoEmbed(vimeoManage[1], vimeoManage[2])

  // Vimeo URL with hash as path segment
  const vimeoPathHash = url.match(/vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/)
  if (vimeoPathHash) return vimeoEmbed(vimeoPathHash[1], vimeoPathHash[2])

  // Vimeo URL with hash as query param
  const vimeoQueryHash = url.match(/vimeo\.com\/(\d+)[^/]*[?&]h=([a-zA-Z0-9]+)/)
  if (vimeoQueryHash) return vimeoEmbed(vimeoQueryHash[1], vimeoQueryHash[2])

  // Bare Vimeo ID with hash query
  const idHash = url.match(/^(\d+)\?h=([a-zA-Z0-9]+)$/)
  if (idHash) return vimeoEmbed(idHash[1], idHash[2])

  // Vimeo URL without a hash (public video)
  const vimeoIdOnly = url.match(/vimeo\.com\/(\d+)/)
  if (vimeoIdOnly) return vimeoEmbed(vimeoIdOnly[1], null)

  // Bare numeric Vimeo ID
  if (/^\d+$/.test(url)) return vimeoEmbed(url, null)

  // YouTube watch / short / embed URLs
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/)
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`

  // Last resort — pass through only player/embed hosts in our allowlist. A bare
  // vimeo.com *website* URL is deliberately NOT here: it has no numeric id (the
  // branches above would have caught one), so it is an unrecognized link and
  // returns null rather than loading an un-framable Vimeo web page.
  const ALLOWED_HOSTS = ["youtube.com", "youtube-nocookie.com", "youtu.be", "player.vimeo.com"]
  try {
    const parsed = new URL(url)
    if (ALLOWED_HOSTS.some((host) => parsed.hostname.endsWith(host))) return url
  } catch {
    // not a URL
  }
  return null
}
